import { fabric } from 'fabric';
import CollaborationBehaviors from './CollaborationBehaviors';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import LockBehaviors from './LockBehaviors';
import { pointInRectWithRadius } from '../utils/geometry';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { hasCommentableBehaviors, isFrameLike } from './guards';
import { type UserInfo, type RecordId } from '../types';
import type CommentableBehaviors from './CommentableBehaviors';

const COMMENT_SIZE = 40;
const UNSELECTABLE_OPACITY = 0.65;
const DEFAULT_OPACITY = 1;

interface AvatarInfo {
  image?: HTMLImageElement;
  initials: string;
}
// A comment - used to annotate the canvas

interface Comment
  extends fabric.Object,
    LockBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors {}

@LockBehaviors
@AnalyticsBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
class Comment extends fabric.Object {
  type = 'comment';
  noScaleCache = false;
  objectCaching = false;
  // Use mousedown events to make comments only selectable on click
  selectable = false;
  opacity = DEFAULT_OPACITY;

  uniformScaling = true;
  snapAngle = 45;
  snapThreshold = 5;

  _overCommentable: (CommentableBehaviors & fabric.Object) | null;

  // Comment objects can't be resized or rotated
  controls = {};

  width = COMMENT_SIZE;
  height = COMMENT_SIZE;
  strokeWidth = 2;
  padding = 0;

  commentNumber: number | null = null;
  annotationId: RecordId | null = null;
  isSaving: boolean = false;
  resolved: boolean = false;
  focusHighlight: boolean = false;
  ownPadding: number = 0;
  // So we can check if the comment was created in this whiteboard session.
  annotationsManagerId: string;
  hideObjectToolbar: boolean = false;
  authorId: RecordId;
  ownerUserId?: RecordId | undefined;

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'left',
    'top',
    'authorId',
    'annotationId',
    'resolved',
    'commentNumber',
    'commentable',
    'annotationsManagerId',
    'ownerUserId',
  ];

  authorInfo: UserInfo | null = null;

  commentable: string | null = null;
  _avatar: AvatarInfo | null = null;

  // We only want to open the sidebar if the user makes a single click on
  // a comment but not if they are dragging it.
  _shouldOpenSidebar = false;

  constructor(options) {
    super({
      ...options,
    });

    this.commentNumber = options.commentNumber || null;
    this.annotationId = options.annotationId;
    this.resolved = options.resolved || false;
    this.commentable = options.commentable || null;

    if (this.annotationId) {
      // Add the annotation ID to the cache
      this.controller.actionAddAnnotationId(this.annotationId);
    }

    this.updateVisibility();
    this.initEvents();
    this.initializeLockBehaviors(options.locked);

    // Prevent the object toolbar showing and any movement on an unsaved
    // comment so we don't add to the undo stack.
    // This has to come after initializeLockBehaviors.
    if (!this.annotationId) {
      this.hideObjectToolbar = true;
      this.lockMovementX = true;
      this.lockMovementY = true;
    }
  }

  static commentSize() {
    return COMMENT_SIZE;
  }

  get belongsToCurrentUser(): boolean {
    return this.controller.state.userId === this.ownerId;
  }

  get ownerId(): RecordId {
    // For comments created after CREATE-1364 shipped, the ownerUserId is
    // the source of truth for the comments owner.
    // For older comments, fallback to the authorId.
    return this.ownerUserId ?? this.authorId;
  }

  async resolveUserInfo(): Promise<{
    displayName: string;
    avatar?: string;
  } | null> {
    if (!this.annotationId && !this.belongsToCurrentUser) {
      return null;
    }

    try {
      this.authorInfo =
        (await this.controller.actionResolveUserInfo(this.ownerId)) ?? null;
    } catch (e) {
      console.error('could not resolve author info', e);
    }
    return this.authorInfo;
  }

  tools(): null {
    return null;
  }

  focusInSidePanel(delay: number = 0): void {
    this.controller.actionOpenSidePanel('comments');

    setTimeout(() => {
      this.controller.annotationsManager.updateAnnotationSidebarFocusStatus(
        this.annotationId,
        true
      );
    }, delay);
  }

  initEvents(): void {
    this.on('created', () => {
      this.addToCommentable();
      if (this.annotationId) {
        this.controller.annotationsManager.updateAnnotationHiddenStatus(
          this.annotationId,
          false
        );
      }
    });
    this.on('deleted', () => {
      if (this.annotationId) {
        this.controller.annotationsManager.updateAnnotationHiddenStatus(
          this.annotationId,
          true
        );
      }
    });

    this.on('mousedown:before', () => {
      this.selectable = true;
      // Make sure the comment appears selectable
      this.opacity = DEFAULT_OPACITY;
    });

    this.on('mousedown', () => {
      this._shouldOpenSidebar = true;
    });

    this.on('mouseup', () => {
      if (this._shouldOpenSidebar) {
        this.focusInSidePanel();
        this._shouldOpenSidebar = false;
      }
    });

    this.on('selected', () => {
      this.focusHighlight = true;
    });

    this.on('deselected', () => {
      this.focusHighlight = false;
      if (this.annotationId) {
        this.controller.annotationsManager.updateAnnotationSidebarFocusStatus(
          this.annotationId,
          false
        );
      } else {
        this.controller.annotationsManager.cancelAnnotation(this);
      }
    });

    this.on('moving', e => {
      if (!e.commentableMoving && this.allowCommentableUpdate()) {
        this.highlightCommentable(e.e);
      }
    });

    this.on('moved', e => {
      this._shouldOpenSidebar = false;
      if (!e.commentableMoving && this.allowCommentableUpdate()) {
        this.addToCommentable(e.e);
      }
    });
  }

  initGroupSelectorListeners() {
    if (this.canvas) {
      const handleUpdate = () => {
        if (
          this.canvas.hasGroupSelector &&
          this.opacity !== UNSELECTABLE_OPACITY
        ) {
          this.opacity = UNSELECTABLE_OPACITY;
          this.canvas.requestRenderAll();
        }
      };
      const handleCleared = () => {
        if (this.opacity !== DEFAULT_OPACITY) {
          this.opacity = DEFAULT_OPACITY;
          this.canvas.requestRenderAll();
        }
        this.selectable = false;
      };

      this.canvas.on('groupSelector:updated', handleUpdate);
      this.canvas.on('groupSelector:cleared', handleCleared);

      this.on('removed', () => {
        this.canvas.off('groupSelector:updated', handleUpdate);
        this.canvas.off('groupSelector:cleared', handleCleared);
      });
    }
  }

  allowCommentableUpdate() {
    const activeObject = this.controller.canvas.getActiveObject();
    return activeObject?.type === 'comment';
  }

  // Add the object to a commentable if it is over one.
  addToCommentable(e?: MouseEvent): void {
    const commentable = this.overCommentable(e);

    if (commentable && this.commentable !== commentable.id) {
      this.set('commentable', commentable.id);
    } else if (!commentable && this.commentable) {
      this.set('commentable', null);
    }

    this._overCommentable?.clearCommentHovered();
    this._overCommentable = null;
  }

  // Show a visible indicator on the commentable
  highlightCommentable(e?: MouseEvent): void {
    const obj = this.overCommentable(e);
    this._overCommentable?.clearCommentHovered();
    if (obj) {
      this._overCommentable = obj;
      this._overCommentable.setCommentHovered();
    } else {
      this._overCommentable = null;
    }
  }

  overCommentable(e?: MouseEvent) {
    // Find the first commentable object that contains this comment
    return this.controller.canvas.findFirstObject(
      (obj): obj is fabric.Object & CommentableBehaviors => {
        if (!hasCommentableBehaviors(obj)) return false;

        // use the mouse position to determine if the comment is over a commentable,
        // falling back to overlapsObject for undo / paste operations

        if (!e) {
          return obj.overlapsObject(this);
        }

        const point = this.canvas.getPointer(e) as fabric.Point;

        if (isFrameLike(obj) && obj.everContainsPoint) {
          return obj.everContainsPoint(point, null, true, true);
        }

        return obj.containsPoint(point, null, true, true);
      }
    );
  }

  setAnnotation(annotationId: RecordId, commentNumber: number): void {
    this.set({
      annotationId,
      commentNumber,
      isSaving: false,
      lockMovementX: false,
      lockMovementY: false,
      hideObjectToolbar: false,
    });
    this.controller.actionAddAnnotationId(annotationId);
    this.controller.canvas.requestRenderAll();
  }

  setResolved(resolved: boolean): void {
    this.set('resolved', resolved);
    this.updateVisibility();
    if (resolved) {
      this.controller.removeHiddenCommentsFromSelection();
    }
    this.controller.canvas.requestRenderAll();
  }

  updateVisibility(): void {
    const { showResolvedComments } = this.controller.state;
    const { commentsVisible, commentsEnabled } = this.controller.props;
    let visible = commentsEnabled && commentsVisible;

    if (this.resolved && !showResolvedComments) {
      visible = false;
    }
    if (
      !this.annotationId &&
      this.controller.annotationsManager.annotationsManagerId !==
        this.annotationsManagerId
    ) {
      visible = false;
    }
    this.set('visible', visible);

    // If the comment is part of a group, sync the group objects so that
    // the comment is included/excluded depending on its visibility.
    if (this.simpleGroup) {
      this.simpleGroup.syncFromObjects();
    }
  }

  setFromRemote(key: KeyPath, value: any, options?: Record<string, any>): void {
    super.setFromRemote(key, value, options);

    // If this is a local undo/redo operation on resolved, update the resolved
    // status in the side panel.
    if (options?.isSource && key === 'resolved') {
      this.controller.annotationsManager.updateAnnotationResolvedStatus(
        this.annotationId as RecordId,
        Boolean(value)
      );
    }

    // The comment is being updated with an annotationId - it should be fully
    // interactive. Make sure the comment is not in an unmovable 'saving' state
    if (key === 'annotationId' && value) {
      this.hideObjectToolbar = false;
      this.lockMovementX = false;
      this.lockMovementY = false;
    }

    // If resolved or annotationId have changed, update the comment visibility.
    if (typeof key === 'string' && ['resolved', 'annotationId'].includes(key)) {
      this.updateVisibility();
    }
  }

  containsPoint(pointer) {
    // Translate the absolute mouse position to the viewport.
    const adjustedPointer = this.canvas.restorePointerVpt(pointer);

    // Get the distance from the mouse to the center of the object.
    const distance = Math.hypot(
      adjustedPointer.x - (this.left + this.width / 2),
      adjustedPointer.y - (this.top + this.height / 2)
    );

    let zoom = this.canvas.getZoom();
    if (zoom < 1) zoom = (zoom + 1) / 2;

    return distance <= COMMENT_SIZE / 2 / zoom;
  }

  intersectsWithRect(pointTL, pointBR) {
    let zoom = this.canvas.getZoom();
    if (zoom < 1) zoom = (zoom + 1) / 2;

    return pointInRectWithRadius(
      { x: this.left + this.width / 2, y: this.top + this.height / 2 },
      {
        left: pointTL.x,
        top: pointTL.y,
        width: pointBR.x - pointTL.x,
        height: pointBR.y - pointTL.y,
      },

      COMMENT_SIZE / 2 / zoom
    );
  }

  _set(key: string, value: any): fabric.Object {
    // We ignore scaling changes when the comment is scaled as part of a group.
    if (['scaleX', 'scaleY'].includes(key)) return this;

    super._set(key, value);

    if (key === 'canvas' && value) {
      this.initGroupSelectorListeners();
    }

    return this;
  }

  avatarInitials(name?: string): string {
    // Initials logic from aha-avatar web component
    let initials = 'N/A';
    if (name) {
      // Fallback to initials based on the name
      const names = name.trim().split(/\s+/);
      // Remove the middle names
      names.splice(1, names.length - 2);
      initials = names.map(w => w[0].toUpperCase()).join('');
    }
    return initials;
  }

  _renderAvatar(ctx: CanvasRenderingContext2D, zoom: number): void {
    if (!this._avatar) {
      void this.resolveUserInfo().then(authorInfo => {
        const avatar: AvatarInfo = {
          initials: this.avatarInitials(authorInfo?.displayName),
        };

        if (authorInfo?.avatar) {
          const image = (avatar.image = new Image());
          image.onload = () => {
            this.controller.canvas.requestRenderAll();
          };
          image.src = authorInfo.avatar;
        }

        this._avatar = avatar;

        this.controller.canvas.requestRenderAll();
      });
    } else if (this._avatar.image) {
      this._renderAvatarImage(ctx, zoom);
    } else {
      this._renderAvatarInitials(ctx, zoom);
    }
  }

  _renderAvatarInitials(ctx: CanvasRenderingContext2D, zoom: number): void {
    if (!this._avatar) return;

    const borderWidth = 3;

    ctx.save();
    ctx.scale(1 / zoom, 1 / zoom);
    ctx.translate(-this.width / 2, -this.height / 2);
    ctx.beginPath();
    ctx.arc(
      this.width / 2,
      this.height / 2,
      this.width / 2 - borderWidth,
      0,
      Math.PI * 2
    );
    ctx.closePath();

    ctx.fillStyle = '#cdcdcd';
    ctx.fill();

    ctx.font = '12pt Inter, Helvetica, "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(this._avatar.initials, this.width / 2, this.height / 2 + 1);

    ctx.restore();
  }

  _renderAvatarImage(ctx: CanvasRenderingContext2D, zoom: number): void {
    if (!this._avatar) return;

    const { image } = this._avatar;
    if (!image) return;

    const borderWidth = 3;

    if (image.complete) {
      ctx.save();
      ctx.scale(1 / zoom, 1 / zoom);
      ctx.translate(-this.width / 2, -this.height / 2);
      ctx.beginPath();
      ctx.arc(
        this.width / 2,
        this.height / 2,
        this.width / 2 - borderWidth,
        0,
        Math.PI * 2
      );
      ctx.closePath();
      ctx.clip();

      try {
        ctx.drawImage(
          image,
          borderWidth,
          borderWidth,
          this.width - borderWidth * 2,
          this.height - borderWidth * 2
        );
      } catch {
        // Handle errors rendering the avatar image, revert back to initials
        this._avatar.image = undefined;
        this.controller.canvas.requestRenderAll();
      }
      ctx.restore();
    }
  }

  _render(ctx): void {
    if (this.canvas.exporting) return;

    let zoom = this.canvas.getZoom();
    if (zoom < 1) zoom = (zoom + 1) / 2;

    ctx.save();
    // If user has created a drag selection, update opacity to indicate comment
    // is not selectable
    ctx.globalAlpha = this.opacity;

    ctx.save();
    ctx.scale(1 / zoom, 1 / zoom);
    ctx.translate(-this.width / 2, -this.height / 2);
    ctx.beginPath();
    ctx.arc(this.width / 2, this.height / 2, this.width / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.shadowColor = 'rgba(0, 0, 0, .6)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.scale(1 / zoom, 1 / zoom);
    ctx.translate(-this.width / 2, -this.height / 2);
    ctx.beginPath();
    ctx.arc(this.width / 2, this.height / 2, this.width / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.strokeStyle = this.focusHighlight ? '#0073CF' : '#ffffff';
    ctx.lineWidth = this.strokeWidth;
    ctx.stroke();
    ctx.restore();

    this._renderAvatar(ctx, zoom);

    ctx.restore();
  }

  // Don't draw borders or controls since we are rendering the selection
  // state ourselves based on this.focusHighlight.
  _renderControls() {}

  showLockTool() {
    return false;
  }
}

export default Comment;
